import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { useTheme } from '@mui/joy';

import { MOTION, CHAT } from './chatTheme';

/**
 * Анимация отправки сообщения.
 *
 * Текст из поля ввода сжимается в «комочек», по дуге улетает ВПРАВО к месту,
 * где появится пузырь, и там разворачивается обратно.
 *
 * Почему элемент позиционируется по ЦЕНТРУ (left/top = центр старта плюс
 * transform: translate(-50%, -50%)): в первой версии он крепился левым верхним
 * углом, и при сжатии ширины «схлопывался» влево — со стороны это выглядело
 * так, будто сообщение улетает в середину чата, а не вправо. Теперь сжатие
 * идёт к центру, и видно ровно то, что задумано.
 *
 * Рисуется порталом в document.body: полёт идёт поверх всех контейнеров с
 * overflow, иначе комочек обрезался бы границей поля ввода.
 *
 * Уважает prefers-reduced-motion — при включённой настройке полёта нет.
 */

const CRUMPLE = 0.32;   // доля времени на сжатие в комочек
const BALL = 40;        // диаметр комочка, px

export function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Снимок геометрии элемента — то, что нужно для старта/финиша полёта. */
export function captureRect(el) {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (!r.width || !r.height) return null;
  return {
    left: r.left, top: r.top, width: r.width, height: r.height,
    cx: r.left + r.width / 2, cy: r.top + r.height / 2,
  };
}

export default function SendFlight({ flight, onDone }) {
  const theme = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  const gradient = theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, #4f46e5 0%, #6d3fd6 100%)'
    : 'linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)';

  const geom = useMemo(() => {
    if (!flight?.from || !flight?.to) return null;
    const { from, to } = flight;
    // Считаем от ЦЕНТРА поля ввода к ЦЕНТРУ будущего пузыря.
    const dx = to.cx - from.cx;
    const dy = to.cy - from.cy;
    // Подъём дуги. Полёт всегда идёт вверх-вправо, поэтому дуга задирается
    // над прямой — так движение читается как бросок, а не как рывок.
    const lift = Math.min(150, Math.max(70, Math.abs(dx) * 0.28 + 70));
    const landW = Math.max(BALL, Math.min(from.width * 0.7, to.width || 240));
    const landH = Math.max(BALL, Math.min(from.height, 46));
    return { from, dx, dy, lift, landW, landH };
  }, [flight]);

  if (!mounted || !flight || !geom) return null;

  const { from, dx, dy, lift, landW, landH } = geom;
  const duration = MOTION.flight;

  return createPortal(
    <>
      <motion.div
        key={flight.id}
        aria-hidden
        initial={{
          x: 0, y: 0,
          width: from.width, height: from.height,
          borderRadius: CHAT.bubbleRadius,
          opacity: 1, rotate: 0,
        }}
        animate={{
          // Дуга вправо-вверх и вниз к цели
          x: [0, dx * 0.45, dx],
          y: [0, dy * 0.25 - lift, dy],
          // Сжатие в комочек и разворот обратно в пузырь
          width: [from.width, BALL, landW],
          height: [from.height, BALL, landH],
          borderRadius: [CHAT.bubbleRadius, BALL / 2, CHAT.bubbleRadius],
          rotate: [0, -12, 0],
          opacity: [1, 1, 0],
        }}
        transition={{
          duration,
          times: [0, CRUMPLE, 1],
          // Три ключевых кадра = два отрезка, значит и функций плавности две:
          // «затягивание» при сжатии и мягкое приземление на второй половине.
          ease: [MOTION.easeIn, MOTION.ease],
          opacity: { duration, times: [0, 0.86, 1], ease: 'linear' },
        }}
        onAnimationComplete={onDone}
        style={{
          position: 'fixed',
          // Крепим по центру: ширина и высота меняются симметрично,
          // и комочек схлопывается ровно в точку старта.
          left: from.cx,
          top: from.cy,
          translateX: '-50%',
          translateY: '-50%',
          zIndex: 12000,
          pointerEvents: 'none',
          background: gradient,
          boxShadow: '0 8px 28px rgba(79,70,229,0.42)',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          padding: `0 ${CHAT.bubblePadX}px`,
          willChange: 'transform, width, height',
        }}
      >
        {/* Текст живёт только в первой трети полёта — он «скручивается» */}
        <motion.span
          initial={{ opacity: 1, scale: 1 }}
          animate={{
            opacity: [1, 0, 0],
            scale: [1, 0.5, 0.5],
            filter: ['blur(0px)', 'blur(2px)', 'blur(2px)'],
          }}
          transition={{ duration, times: [0, CRUMPLE * 0.75, 1], ease: 'easeIn' }}
          style={{
            color: '#fff',
            fontSize: 14,
            lineHeight: 1.35,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '100%',
            display: 'block',
          }}
        >
          {flight.text}
        </motion.span>
      </motion.div>

      {/* Догоняющий блик — короткий след за комочком */}
      <motion.div
        key={`${flight.id}-trail`}
        aria-hidden
        initial={{ x: 0, y: 0, opacity: 0, scale: 0.4 }}
        animate={{
          x: [0, dx * 0.45, dx],
          y: [0, dy * 0.25 - lift, dy],
          opacity: [0, 0.5, 0],
          scale: [0.4, 1.15, 0.5],
        }}
        transition={{ duration: duration * 1.05, times: [0, CRUMPLE, 1], ease: MOTION.ease }}
        style={{
          position: 'fixed',
          left: from.cx,
          top: from.cy,
          translateX: '-50%',
          translateY: '-50%',
          width: 54, height: 54, borderRadius: '50%',
          zIndex: 11999,
          pointerEvents: 'none',
          background: 'radial-gradient(circle, rgba(124,58,237,0.55) 0%, rgba(124,58,237,0) 70%)',
          filter: 'blur(3px)',
        }}
      />
    </>,
    document.body,
  );
}
